import { Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, MapPin, CheckCircle2 } from "lucide-react";
import { metaTrack } from "@/lib/analytics";

const CPS_STYLE = {
    "--cps-forest": "#0F2A22",
    "--cps-forest-2": "#143A2C",
    "--cps-emerald": "#1F6E4C",
    "--cps-lime": "#A7C957",
    "--cps-cream": "#F5EFD8",
    "--cps-cream-dim": "rgba(245,239,216,0.72)",
    "--cps-border": "rgba(245,239,216,0.18)",
};

const HAWA_MAHAL_IMG =
    "https://images.unsplash.com/photo-1757310062384-d3bcfe3026e8?w=1600&q=80&fm=jpg&fit=crop";

export default function CpsConfirmationPage() {
    // Meta Purchase fallback flush.
    //
    // If the CpsApplyPage `onPaid` handler fired Purchase successfully but the
    // browser dropped the beacon (unmount race / ad-blocker / iOS in-app
    // WebView / flaky network), the "pending_purchase" record it wrote to
    // sessionStorage will still be here. We fire Purchase again using the
    // SAME event_id — Meta will dedupe against the primary fire and against
    // the server-side CAPI fire from /payments/verify or /payments/paypal/capture.
    //
    // This is NOT "fire Purchase because user reached the success URL": the
    // record is written ONLY after backend has independently verified capture.
    // The record is cleared on flush so a bookmark or refresh cannot replay it.
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem("zsgi.pending_purchase.v1");
            if (!raw) return;
            const pending = JSON.parse(raw);
            if (!pending || !pending.event_id || !pending.params) return;
            metaTrack("Purchase", pending.params, {
                dedupKey: `Purchase:${pending.application_id || pending.event_id}`,
                eventId: pending.event_id,
            });
            sessionStorage.removeItem("zsgi.pending_purchase.v1");
        } catch (_e) { /* noop */ }
    }, []);
    return (
        <section
            data-testid="cps-confirmation-page"
            style={CPS_STYLE}
            className="relative min-h-screen pt-28 md:pt-36 pb-24 md:pb-40 flex items-center"
        >
            {/* Background */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, var(--cps-forest) 0%, var(--cps-forest-2) 60%, var(--cps-forest) 100%)" }} />
            <div className="absolute inset-x-0 top-0 h-[70vh] opacity-[0.18]">
                <img src={HAWA_MAHAL_IMG} alt="Hawa Mahal, Jaipur" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 0%, var(--cps-forest) 90%)" }} />
            </div>
            <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 15% 15%, var(--cps-lime) 0, transparent 40%), radial-gradient(circle at 85% 85%, var(--cps-emerald) 0, transparent 50%)" }} />

            <div className="relative max-w-[900px] mx-auto px-6 md:px-12 text-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-8"
                    style={{ background: "rgba(167,201,87,0.2)", border: "1px solid var(--cps-lime)" }}
                    data-testid="cps-confirmed-icon"
                >
                    <CheckCircle2 className="w-8 h-8" style={{ color: "var(--cps-lime)" }} />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="label-mono flex items-center justify-center gap-2 mb-4"
                    style={{ color: "var(--cps-lime)" }}
                >
                    APPLICATION RECEIVED
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.2, ease: [0.76, 0, 0.24, 1] }}
                    className="serif-display text-4xl md:text-6xl leading-[0.95]"
                    style={{ color: "var(--cps-cream)" }}
                    data-testid="cps-confirmed-title"
                >
                    Your Climate Power Summit 2026 application has been{" "}
                    <span className="serif-italic" style={{ color: "var(--cps-lime)" }}>received</span>.
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.35 }}
                    className="mt-6 md:mt-8 text-base md:text-lg max-w-2xl mx-auto"
                    style={{ color: "var(--cps-cream-dim)" }}
                >
                    Your application will now be reviewed for selection and funding category. We will be in touch by email with next steps.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.45 }}
                    className="mt-12 grid grid-cols-1 sm:grid-cols-2 border"
                    style={{ borderColor: "var(--cps-border)" }}
                >
                    <div
                        className="p-6 flex flex-col items-center sm:items-start sm:text-left border-b sm:border-b-0 sm:border-r"
                        style={{ borderColor: "var(--cps-border)" }}
                        data-testid="cps-confirmed-location"
                    >
                        <div className="flex items-center gap-2 label-mono text-[11px]" style={{ color: "var(--cps-lime)" }}>
                            <MapPin className="w-3.5 h-3.5" /> LOCATION
                        </div>
                        <div className="serif-display text-2xl md:text-3xl mt-3" style={{ color: "var(--cps-cream)" }}>
                            Jaipur, India
                        </div>
                    </div>
                    <div
                        className="p-6 flex flex-col items-center sm:items-start sm:text-left"
                        data-testid="cps-confirmed-dates"
                    >
                        <div className="flex items-center gap-2 label-mono text-[11px]" style={{ color: "var(--cps-lime)" }}>
                            <Calendar className="w-3.5 h-3.5" /> DATES
                        </div>
                        <div className="serif-display text-2xl md:text-3xl mt-3" style={{ color: "var(--cps-cream)" }}>
                            13–15 November 2026
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="mt-6 serif-italic text-lg md:text-xl"
                    style={{ color: "var(--cps-lime)" }}
                >
                    People • Planet • Progress
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.75 }}
                    className="mt-12 flex flex-wrap gap-4 justify-center"
                >
                    <Link
                        to="/programs/cps-2026"
                        data-testid="cps-confirmed-explore-btn"
                        className="inline-flex items-center gap-2 px-6 py-3.5 label-mono text-[11px] transition-all hover:scale-[1.02]"
                        style={{ background: "var(--cps-lime)", color: "var(--cps-forest)" }}
                    >
                        Explore the Summit <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <Link
                        to="/"
                        data-testid="cps-confirmed-home-btn"
                        className="inline-flex items-center gap-2 px-6 py-3.5 label-mono text-[11px] transition-all hover:bg-[rgba(167,201,87,0.1)]"
                        style={{ border: "1px solid var(--cps-lime)", color: "var(--cps-cream)" }}
                    >
                        Return home
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}
